import { DEFAULT_LOCALE, LEGACY_LOCALE_PREFIXES, LOCALE_CONFIG, LOCALES } from '#i18n/locale-config';
import type { AppArea } from '@/contexts/app/policy';

/**
 * 路径 → 区域的纯规则。middleware、首帧脚本与客户端共用这一份，顺序即优先级。
 * 不依赖 React、Next 或浏览器，可在 Edge、Node 与内联脚本里求值。
 */
export const AREA_PREFIXES: ReadonlyArray<readonly [AppArea, string]> = [
    ['protocol', '/enter_home_auth'],
    ['protocol', '/component-preview'],
    ['protocol', '/connectors/oauth'],
    ['protocol', '/mcp-auth'],
    ['protocol', '/oauth'],
    ['shared-site', '/agent'],
    ['product', '/workspace'],
    ['product', '/project'],
    ['product', '/project-initialization-preview'],
    ['product', '/settings'],
    ['product', '/onboarding'],
    ['product', '/invite'],
    ['product', '/waitlist'],
    ['product', '/security-notice'],
    ['product', '/403'],
];

/**
 * 语言前缀表，长前缀优先，避免 `/zh` 抢在 `/zh-CN` 之前匹配。
 * 英语没有路径前缀，但 middleware 接受并去掉 `/en`，这里同样视为语言前缀。
 */
export const LOCALE_PATH_PREFIXES: readonly string[] = [...new Set(
    [`/${DEFAULT_LOCALE}`, ...LOCALES.flatMap((locale) => [LOCALE_CONFIG[locale].pathPrefix, LEGACY_LOCALE_PREFIXES[locale]])]
        .filter((prefix): prefix is string => Boolean(prefix)),
)].sort((a, b) => b.length - a.length);

function stripLocalePrefix(path: string): string {
    for (const prefix of LOCALE_PATH_PREFIXES) {
        if (path === prefix || path.startsWith(`${prefix}/`)) return path.slice(prefix.length) || '/';
    }
    return path;
}

function matchArea(path: string): AppArea | null {
    for (const [area, prefix] of AREA_PREFIXES) {
        if (path === prefix || path.startsWith(`${prefix}/`)) return area;
    }
    return null;
}

function toPathname(href: string): string {
    const pathname = href.split(/[?#]/, 1)[0] || '/';
    return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

/**
 * 产品区、访客站点与协议页都不使用语言前缀；带前缀的入口按真实语言表剥离后再判定。
 * 首帧脚本序列化的是同一张前缀表，服务端与客户端结论一致。
 */
export function resolveAppArea(href: string): AppArea {
    const path = toPathname(href);
    return matchArea(path) ?? matchArea(stripLocalePrefix(path)) ?? 'public';
}

/**
 * 带语言前缀的非公开路径（`/zh/workspace`）。这类 URL 过去落到 Web 的 404，
 * 迁移后由 middleware 308 到无前缀路径，语言仍以 Cookie 为准。
 */
export function getUnprefixedNonPublicPath(href: string): string | null {
    const path = toPathname(href);
    if (matchArea(path)) return null;
    const stripped = stripLocalePrefix(path);
    return stripped !== path && matchArea(stripped) ? stripped : null;
}
